import { useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowUpDown,
  Filter,
  Search,
  RefreshCw,
  CalendarClock,
  XCircle,
  Clock,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Send,
  Eye,
  Loader2,
  Home,
  History,
  Calendar as CalendarIcon,
  Pencil,
  CheckCircle,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { scheduledOrdersExportApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/formatters";
import { ScheduledOrder, Client } from "@/lib/api";
import { useScheduledOrderManagement } from "@/hooks/useScheduledOrderManagement";
import { useUser } from "@/contexts/UserContext";
import { ErrorState } from "@/components/shared/ErrorState";
import { Skeleton } from "@/components/ui/skeleton";
import { TooltipWrapper } from "@/components/shared/TooltipWrapper";
import { ScheduledOrderDetailsModal } from "@/components/OrderInterface/Scheduled-Order/ScheduledOrderDetailsModal";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import StateBadge from "@/components/ui/stateBadge";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ScheduledOrderCycleSelector } from "@/components/ScheduledOrders/ScheduledOrderCycleSelector";
import { ScheduledOrderCycleFilter } from "@/components/ScheduledOrders/ScheduledOrderCycleFilter";
import { isScheduledOrderClientViewEnabled } from "@/features/scheduledOrderClientViewFeature";
import { isScheduledOrderStateBadgesEnabled } from "@/features/scheduledOrderStateBadgesFeature";
import { isScheduledOrderCreatedByColumnEnabled } from "@/features/scheduledOrderCreatedByColumnFeature";
import { isOpenCycleOnlyScheduledOrdersEnabled } from "@/features/openCycleOnlyScheduledOrdersFeature";
import { isNewScheduledOrderCycleEnabledForCycle } from "@/features/newScheduledOrderCycleFeature";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SYSTEM_CREATED_BY_LABEL = "Automático/Sistema";

const ScheduledOrderSkeleton = () => (
  <div className="min-h-screen bg-background">
    <header className="border-b bg-foreground text-background">
      <div className="container mx-auto px-6 py-4 space-y-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-32 bg-muted" />
          <Skeleton className="h-8 w-64 bg-muted" />
          <Skeleton className="h-4 w-40 bg-muted" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="bg-muted border-border">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <Skeleton className="h-3 w-20 bg-muted" />
                    <Skeleton className="h-8 w-12 bg-muted" />
                  </div>
                  <Skeleton className="h-8 w-8 rounded-full bg-muted" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </header>
    <main className="container mx-auto px-6 py-8 space-y-6">
      <Card>
        <CardHeader><Skeleton className="h-6 w-40" /></CardHeader>
        <CardContent><div className="grid gap-4 md:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div></CardContent>
      </Card>
      <Card>
        <CardContent className="p-0">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>{Array.from({ length: 7 }).map((_, i) => <TableHead key={i}><Skeleton className="h-5 w-24" /></TableHead>)}</TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>{Array.from({ length: 7 }).map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </main>
  </div>
);

export default function ScheduledOrderPage() {
  const {
    tableData,
    totalRows,
    totalPages,
    isLoading,
    error,
    filters,
    setFilters,
    sorting,
    setSorting,
    pagination,
    setPagination,
    handleClearFilters,
    allOrders,
    sendOrders,
    isLoadingSend,
    sendIndividualOrder,
    sendIndividualOrderClient,
    reprocessSelectedOrders,
    deleteSelectedOrders,
    isProcessingAction,
    availableCycleYears,
    activeCycleYear,
    selectedCycleYear,
    setSelectedCycleYear,
  } = useScheduledOrderManagement(undefined, "backoffice");

  const [isExporting, setIsExporting] = useState(false);

  // Exporta todos os pedidos programados do ciclo selecionado (todas as escolas).
  // A API devolve um .xlsx com as abas "Pedidos" e "Itens".
  const handleExportCycle = async () => {
    if (!selectedCycleYear) return;
    setIsExporting(true);
    try {
      const blob = await scheduledOrdersExportApi.exportCycle(selectedCycleYear);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `pedidos-programados-${selectedCycleYear}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erro ao exportar pedidos programados:", err);
      toast.error("Não foi possível exportar os pedidos programados.");
    } finally {
      setIsExporting(false);
    }
  };

  const { setSelectedClient } = useUser();

  const [rowSelection, setRowSelection] = useState({});
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const navigate = useNavigate();
  const [detailsModal, setDetailsModal] = useState<{
    isOpen: boolean;
    clientId: string | null;
    clientName: string | null;
    orderNumber: string | null;
    orderPayload: any | null;
    clientCode: string | null;
    cycleCode: string | number | null;
    scheduledOrderId: string | null;

  }>({
    isOpen: false,
    clientId: null,
    clientName: null,
    orderNumber: null,
    orderPayload: null,
    clientCode: null,
    cycleCode: null,
    scheduledOrderId: null,
  });

  const metrics = useMemo(() => {
    return allOrders.reduce(
      (acc, order) => {
        const status = order?.status?.toLowerCase();
        // Ignorar pedidos finalizados ou cancelados das métricas de acompanhamento
        if (!status || status === 'completed' || status === 'cancelled') return acc;

        // Simulados dos ciclos 2027+ seguem o fluxo do 2º semestre; só os antigos entram no card "simulado".
        const isSimulado = (`${order?.cycleCode}`.includes('00') || (order?.orderNumber || '').startsWith('PED-SIM'))
          && !isNewScheduledOrderCycleEnabledForCycle(Number(String(order?.cycleCode || '').slice(0, 4)));

        // Contagem de falhas (independente do tipo)
        if (status === 'failed') {
          acc.failed++;
        }

        if (isSimulado) {
          acc.simulado++;
        } else {
          // Apenas pedidos que NÃO são simulados (2º Semestre) entram nestas métricas de status
          if (status === 'confirmed' || status === 'processed') {
            acc.confirmed++;
          } else if (status === 'pending_confirmation' || status === 'pending') {
            acc.pendingConfirmation++;
          }
        }

        return acc;
      },
      { simulado: 0, confirmed: 0, pendingConfirmation: 0, failed: 0 }
    );
  }, [allOrders]);

  const hasActiveFilters = useMemo(() => {
    return filters.searchQuery !== "" ||
      filters.status !== "all" ||
      filters.orderType !== "all" ||
      !!filters.deadlineRange?.from ||
      (isOpenCycleOnlyScheduledOrdersEnabled && !!activeCycleYear && selectedCycleYear !== activeCycleYear);
  }, [filters, activeCycleYear, selectedCycleYear]);

  const editOrder = (order: ScheduledOrder) => {
    const client: Client = {
      id: Number(order.clientId),
      clientId: Number(order.clientId),
      codigo_cnx: order.clientCode,
      nome_cliente: order.clientName,
    };

    setSelectedClient(client);

    navigate('/customer-portal/realizar-pedido', {
      state: {
        typePedido: 'PROGRAMADO',
        isBackofficeEdit: true,
        returnTo: '/backoffice/pedido-programados',
        originalOrderNumber: order.orderNumber,
        scheduledOrderId: order.id,
      }
    })
  }

  const columns: ColumnDef<ScheduledOrder>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && "indeterminate")}
          onCheckedChange={(value) => {
            table.getRowModel().rows.forEach(row => {
              const status = row.original.status?.toLowerCase();
              if (status !== 'completed' && status !== 'cancelled') {
                row.toggleSelected(!!value);
              }
            });
          }}
          aria-label="Selecionar todas as linhas desta página para ações em lote"
        />
      ),
      cell: ({ row }) => {
        const status = row.original.status?.toLowerCase();
        return (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            disabled={status === 'completed' || status === 'cancelled'}
            className={cn((status === 'completed' || status === 'cancelled') && "opacity-50 cursor-not-allowed")}
            aria-label={`Selecionar pedido ${row.original.orderNumber}`}
          />
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "orderNumber",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          aria-label="Número do Pedido: clique para ordenar"
        >
          Núm. Pedido <ArrowUpDown className="ml-2 h-4 w-4" aria-hidden="true" />
        </Button>
      ),
      cell: ({ row }) => <div className="font-mono">{row.original.orderNumber}</div>
    },
    {
      accessorKey: "clientName",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          aria-label="Nome do Cliente: clique para ordenar"
        >
          Cliente <ArrowUpDown className="ml-2 h-4 w-4" aria-hidden="true" />
        </Button>
      ),
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.clientName}</div>
          <div className="text-xs text-muted-foreground">{row.original.clientCode}</div>
        </div>
      )
    },
    ...(isScheduledOrderCreatedByColumnEnabled ? [
    {
      accessorKey: "createdBy",
      header: "Criado Por",
      cell: ({ row }) => {
        const value = row.original.createdBy?.trim() ?? "";

        if (!EMAIL_PATTERN.test(value)) {
          return <span className="text-muted-foreground">{SYSTEM_CREATED_BY_LABEL}</span>;
        }

        return (
          <TooltipWrapper content={value}>
            <span className="cursor-help">
              {value.length > 12 ? `${value.substring(0, 12)}...` : value}
            </span>
          </TooltipWrapper>
        );
      }
    },
    ] : []),
    {
      accessorKey: "orderType",
      header: "Tipo do Pedido",
      cell: ({ row }) => {
        const isSimulado = `${row.original.cycleCode}`.includes('00') || (row.original.orderNumber || '').startsWith('PED-SIM');
        return <>
          {isSimulado ? (
            <div className="flex flex-col gap-0.5">
              <Badge variant="secondary" className="flex w-fit items-center gap-1">Avaliações e Simulados</Badge>
              <span className="text-[12px] text-muted-foreground font-medium pl-1">
                {isScheduledOrderClientViewEnabled
                  ? (row.original.shipmentBlock != null ? `Remessa ${row.original.shipmentBlock}` : null)
                  : row.original.awaitingComposition
                    ? "Aguardando composição"
                    : `Remessa ${row.original.shipmentBlock ?? ""}`.trim()}
              </span>
            </div>
          ) : (
            <Badge
              variant={isSimulado ? 'outline' : 'default'}
              className="w-max"
            >
              2º Semestre
            </Badge>
          )}
        </>
      }
    },
    // A coluna "Cupom" saiu da lista de pedidos programados (20/10).
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const isSimulado = `${row.original.cycleCode}`.includes('00') || (row.original.orderNumber || '').startsWith('PED-SIM');
        const usesAuthoritativeFlow = isNewScheduledOrderCycleEnabledForCycle(Number(String(row.original.cycleCode || '').slice(0, 4)));
        // "Programado" é legado: só para simulados de ciclos anteriores ao novo fluxo.
        const isActuallySimulado = isSimulado && !usesAuthoritativeFlow && row.original?.status !== 'cancelled';
        return <div className="flex flex-col items-start gap-1">
          <StateBadge status={isActuallySimulado ? 'order_queue' : row.original.status} />
          {isScheduledOrderStateBadgesEnabled && usesAuthoritativeFlow && !isActuallySimulado && row.original.processedPurchaseOrderId && <Badge className="bg-blue-600 text-white">Materializado</Badge>}
          {isScheduledOrderStateBadgesEnabled && usesAuthoritativeFlow && !isActuallySimulado && !row.original.processedPurchaseOrderId && row.original.isFrozen && <Badge variant="secondary">Congelado</Badge>}
          {/* A tag "Acumulando" foi removida da listagem (20/10); Materializado e Congelado seguem. */}
        </div>
      },
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          aria-label="Data de Criação: clique para ordenar"
        >
          Criação <ArrowUpDown className="ml-2 h-4 w-4" aria-hidden="true" />
        </Button>
      ),
      cell: ({ row }) => formatDate(new Date(row.original.createdAt)),
    },
    {
      accessorKey: "deadline",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          aria-label="Data Limite: clique para ordenar"
        >
          Data Limite <ArrowUpDown className="ml-2 h-4 w-4" aria-hidden="true" />
        </Button>
      ),
      cell: ({ row }) => row.original.deadline ? formatDate(new Date(`${row.original.deadline}T00:00:00`)) : "-",
    },
    {
      id: "actions",
      header: () => <div className="text-center sr-only">Ações</div>,
      cell: ({ row }) => (
        <div className="flex justify-center gap-1">
          <TooltipWrapper content="Ver detalhes">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDetailsModal({
                isOpen: true,
                clientCode: row.original.clientCode,
                clientId: row.original.clientId,
                clientName: row.original.clientName,
                orderNumber: row.original.orderNumber,
                orderPayload: row.original.orderPayload,
                cycleCode: row.original.cycleCode,
                scheduledOrderId: row.original.id
              })}
              aria-label={`Ver detalhes do pedido ${row.original.orderNumber}`}
            >
              <Eye className="h-4 w-4" aria-hidden="true" />
            </Button>
          </TooltipWrapper>
          <TooltipWrapper content="Enviar pedido">
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                const confirmed = await confirm({
                  title: "Processar Pedido",
                  description: (
                    <span className="flex flex-col gap-3">
                      <span>
                        Deseja processar o pedido <span className="font-bold text-foreground">{row.original.orderNumber}</span> agora?
                      </span>
                      <span className="text-sm border-l-2 border-amber-500 pl-3 py-1 bg-amber-50/50 text-amber-800">
                        Esta ação enviará os itens diretamente para o processamento.
                      </span>
                    </span>
                  ) as any,
                  confirmLabel: "Sim, Processar",
                  cancelLabel: "Cancelar"
                });
                if (confirmed) sendIndividualOrder(row.original.id);
              }}
              disabled={isProcessingAction || !!row.original.processedPurchaseOrderId || row.original.status === 'completed' || row.original.status === 'cancelled'}
              aria-label={`Enviar pedido ${row.original.orderNumber} para processamento`}
            >
              {isProcessingAction ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4" aria-hidden="true" />}
            </Button>
          </TooltipWrapper>
          {row.original.status === 'pending_confirmation'
            && (!(row.original.orderNumber || '').startsWith('PED-SIM')
              || isNewScheduledOrderCycleEnabledForCycle(Number(String(row.original.cycleCode || '').slice(0, 4)))) && (
              <TooltipWrapper content="Confirmar pedido">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    const confirmed = await confirm({
                      title: "Confirmar Pedido Programado",
                      description: `Deseja confirmar o pedido ${row.original.orderNumber}? A composição será congelada para processamento.`,
                      confirmLabel: "Sim, Confirmar",
                      cancelLabel: "Cancelar"
                    });
                    if (confirmed) await sendIndividualOrderClient(row.original.id, row.original);
                  }}
                  disabled={isProcessingAction}
                  aria-label={`Confirmar pedido ${row.original.orderNumber}`}
                >
                  {isProcessingAction
                    ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    : <CheckCircle className="h-4 w-4 text-green-600" aria-hidden="true" />}
                </Button>
              </TooltipWrapper>
            )}
          {row.original.status === 'pending_confirmation'
            && !row.original.isFrozen
            && (!row.original.cutoffAt || new Date(row.original.cutoffAt).getTime() > Date.now()) && (
              <TooltipWrapper content="Editar pedido">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Editar pedido ${row.original.orderNumber}`}
                  className="hover:bg-orange-200"
                  onClick={() => editOrder(row.original)}
                >
                  <Pencil className="h-4 w-4 text-orange-600" aria-hidden="true" />
                </Button>
              </TooltipWrapper>
            )}
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
    manualSorting: false,
    pageCount: totalPages,
    getRowId: (row) => row.id,
    enableRowSelection: (row) => {
      const status = row.original.status?.toLowerCase();
      return status !== 'completed' && status !== 'cancelled';
    },
    state: {
      sorting,
      pagination,
      rowSelection,
    },
  });

  const selectedRows = table.getSelectedRowModel().rows;
  const selectedRowCount = selectedRows.length;
  const selectedOrderIds = selectedRows.map(row => row.original.id);

  const handleReprocessSelected = async () => {
    const confirmed = await confirm({
      title: "Processar Selecionados",
      description: (
        <span className="flex flex-col gap-3">
          <span>Deseja processar os <strong>{selectedRowCount}</strong> pedidos selecionados?</span>

          <span className="max-h-[160px] overflow-y-auto border rounded-md p-2 bg-slate-50/50 block">
            <ul className="space-y-1">
              {selectedRows.map((row) => (
                <li key={row.original.id} className="text-[11px] flex justify-between border-b border-slate-100 last:border-0 pb-1">
                  <span className="font-mono font-bold text-blue-700">{row.original.orderNumber}</span>
                  <span className="text-slate-500 truncate ml-2 max-w-[180px]">{row.original.clientName}</span>
                </li>
              ))}
            </ul>
          </span>

          <span className="text-sm border-l-2 border-amber-500 pl-3 py-1 bg-amber-50/50 text-amber-800">
            Esta ação enviará os itens diretamente para o <strong>processamento</strong>.
          </span>
        </span>
      ) as any,
      confirmLabel: "Sim, Processar",
      cancelLabel: "Cancelar"
    });

    if (confirmed) {
      await reprocessSelectedOrders(selectedOrderIds);
      setRowSelection({});
    }
  };

  const handleProcessAll = async () => {
    const confirmed = await confirm({
      title: "Processar Lote",
      description: "Deseja enviar todos os pedidos pendentes para processamento?",
      confirmLabel: "Sim, Processar Todos",
      cancelLabel: "Cancelar"
    });

    if (confirmed) {
      sendOrders();
    }
  };

  const handleDeleteSelected = async () => {
    const confirmed = await confirm({
      title: "Excluir Pedidos",
      description: `Tem certeza que deseja excluir ${selectedRowCount} pedido(s)? Esta ação é irreversível.`,
      confirmLabel: "Sim, Excluir",
      cancelLabel: "Cancelar"
    });

    if (confirmed) {
      await deleteSelectedOrders(selectedOrderIds);
      setRowSelection({});
    }
  };

  if (isLoading) return <ScheduledOrderSkeleton />;
  if (error) return <ErrorState message="Não foi possível carregar os pedidos programados." details={error.message} />;

  return (
    <>
      <ConfirmDialog />
      <div className="min-h-screen bg-background">
        <header className="border-b " role="banner">
          <div className="container mx-auto px-6 py-4 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="mb-2">
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/customer-portal" className="flex items-center gap-1">
                            <Home className="h-3 w-3" /> Início
                          </Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="text-foreground-600" />
                      <BreadcrumbItem>
                        <BreadcrumbPage className="flex items-center gap-1">
                          <History className="h-3 w-3" /> Pedidos Programados
                        </BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <CalendarClock className="h-6 w-6 text-blue-400" />
                  Pedidos Programados
                </h1>
                <p className="text-slate-500 mt-1" aria-describedby="main-heading">
                  {(metrics.simulado + metrics.pendingConfirmation + metrics.confirmed + metrics.failed)} pedido{allOrders.length !== 1 ? 's' : ''} no total
                </p>
              </div>
            </div>

            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" aria-label="Resumo de métricas">
              {[
                { label: "Avaliações e Simulados", value: metrics.simulado, color: "text-blue-400", icon: CalendarClock },
                { label: "Aguardando confirmação", value: metrics.pendingConfirmation, color: "text-indigo-400", icon: CalendarIcon },
                { label: "Confirmados", value: metrics.confirmed, color: "text-green-400", icon: CheckCircle },
                { label: "Falhas", value: metrics.failed, color: "text-red-400", icon: XCircle }
              ].map((m, i) => (
                <Card key={i} className=" ">
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{m.label}</p>
                        <p className={cn("text-2xl font-bold", m.color)}>{m.value}</p>
                      </div>
                      <m.icon className={cn("h-8 w-8 opacity-50", m.color)} aria-hidden="true" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </section>
          </div>
        </header>

        <main className="container mx-auto px-6 py-8 space-y-6" role="main">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {!isOpenCycleOnlyScheduledOrdersEnabled ? (
              <ScheduledOrderCycleSelector
                years={availableCycleYears}
                activeYear={activeCycleYear}
                selectedYear={selectedCycleYear}
                onSelect={(year) => {
                  setSelectedCycleYear(year);
                  setRowSelection({});
                }}
              />
            ) : <div />}
            <Button
              variant="outline"
              onClick={handleExportCycle}
              disabled={!selectedCycleYear || isExporting}
            >
              {isExporting
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
                : <Download className="h-4 w-4 mr-2" aria-hidden="true" />}
              Exportar para Excel
            </Button>
          </div>

          <Card className={cn("border-border shadow-lg", hasActiveFilters && "border-primary/20 bg-primary/5")}>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Filter className="h-5 w-5" aria-hidden="true" /> Filtros Avançados
                {hasActiveFilters && <Badge variant="secondary" className="ml-2">Ativos</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {isOpenCycleOnlyScheduledOrdersEnabled && (
                  <ScheduledOrderCycleFilter
                    years={availableCycleYears}
                    activeYear={activeCycleYear}
                    selectedYear={selectedCycleYear}
                    onSelect={(year) => {
                      setSelectedCycleYear(year);
                      setRowSelection({});
                    }}
                  />
                )}
                <div className="space-y-2">
                  <Label htmlFor="search-filter">Busca Rápida</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    <Input
                      id="search-filter"
                      placeholder="ID, cliente..."
                      value={filters.searchQuery}
                      onChange={(e) => setFilters(f => ({ ...f, searchQuery: e.target.value }))}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status-filter">Status</Label>
                  <Select value={filters.status} onValueChange={(value) => setFilters(f => ({ ...f, status: value as any }))}>
                    <SelectTrigger id="status-filter" aria-label="Status do pedido">
                      <SelectValue placeholder="Todos os status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os Status</SelectItem>
                      <SelectItem value="order_queue">Programado</SelectItem>
                      <SelectItem value="pending_confirmation">Aguardando confirmação</SelectItem>
                      <SelectItem value="confirmed">Confirmado</SelectItem>
                      <SelectItem value="cancelled">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type-filter">Tipo do Pedido</Label>
                  <Select value={filters.orderType} onValueChange={(value) => setFilters(f => ({ ...f, orderType: value }))}>
                    <SelectTrigger id="type-filter" aria-label="Tipo do pedido">
                      <SelectValue placeholder="Todos os tipos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os Tipos</SelectItem>
                      <SelectItem value="programado">2º Semestre</SelectItem>
                      {Array.from({ length: 8 }, (_, i) => (
                        <SelectItem key={i + 1} value={`remessa-${i + 1}`}>
                          Avaliações e Simulados - Remessa {i + 1}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Data Limite (Período)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !filters.deadlineRange?.from && "text-muted-foreground"
                        )}
                        aria-label="Selecionar período de data limite"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                        {filters.deadlineRange?.from ? (
                          filters.deadlineRange.to ? (
                            <>
                              {format(filters.deadlineRange.from, "dd/MM/yy", { locale: ptBR })} -{" "}
                              {format(filters.deadlineRange.to, "dd/MM/yy", { locale: ptBR })}
                            </>
                          ) : (
                            format(filters.deadlineRange.from, "dd/MM/yy", { locale: ptBR })
                          )
                        ) : (
                          <span>Filtrar por data</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        initialFocus
                        mode="range"
                        defaultMonth={filters.deadlineRange?.from}
                        selected={{
                          from: filters.deadlineRange?.from,
                          to: filters.deadlineRange?.to,
                        }}
                        onSelect={(range) =>
                          setFilters((f) => ({
                            ...f,
                            deadlineRange: {
                              from: range?.from,
                              to: range?.to,
                            },
                          }))
                        }
                        numberOfMonths={2}
                        locale={ptBR}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  variant={!hasActiveFilters ? 'ghost' : 'outline'}
                  size="sm"
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                  className="text-xs"
                >
                  Limpar Filtros
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Listagem de Pedidos</CardTitle>
              {selectedRowCount > 0 && (
                <div className="flex items-center gap-3" role="toolbar" aria-label="Ações em lote">
                  <span className="text-sm font-medium" aria-live="polite">
                    {selectedRowCount} selecionado{selectedRowCount > 1 ? 's' : ''}
                  </span>
                  <Separator orientation="vertical" className="h-6" aria-hidden="true" />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReprocessSelected}
                    disabled={isProcessingAction}
                  >
                    {isProcessingAction ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Processar Pedidos
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-0">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                          const isSortable = header.column.getCanSort();
                          const sortDirection = header.column.getIsSorted();

                          return (
                            <TableHead
                              key={header.id}
                              scope="col"
                              aria-sort={
                                !isSortable
                                  ? undefined
                                  : sortDirection === "asc"
                                    ? "ascending"
                                    : sortDirection === "desc"
                                      ? "descending"
                                      : "none"
                              }
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                            </TableHead>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          data-state={row.getIsSelected() && "selected"}
                          className={cn(
                            row.original.status?.toLowerCase() === 'cancelled' && "opacity-80 grayscale-[0.3] bg-slate-50/30"
                          )}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center"
                        >
                          Nenhum pedido encontrado.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>

            {totalPages > 0 && (
              <nav className="flex items-center justify-between p-4 border-t" aria-label="Navegação de página">
                <div className="text-sm text-muted-foreground">
                  {selectedRowCount} de {totalRows} selecionados
                </div>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="rows-per-page" className="text-sm font-medium">Linhas</Label>
                    <Select
                      value={`${pagination.pageSize}`}
                      onValueChange={value => table.setPageSize(Number(value))}
                    >
                      <SelectTrigger id="rows-per-page" className="h-8 w-[70px]">
                        <SelectValue placeholder={pagination.pageSize} />
                      </SelectTrigger>
                      <SelectContent side="top">
                        {[15, 30, 50, 100].map(pageSize =>
                          <SelectItem key={pageSize} value={`${pageSize}`}>{pageSize}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-sm font-medium">
                    Página {pagination.pageIndex + 1} de {totalPages}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      className="h-8 w-8 p-0"
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                      aria-label="Página anterior"
                    >
                      <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    <Button
                      variant="outline"
                      className="h-8 w-8 p-0"
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                      aria-label="Próxima página"
                    >
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              </nav>
            )}
          </Card>
        </main>

        <ScheduledOrderDetailsModal
          isOpen={detailsModal.isOpen}
          onClose={() => setDetailsModal(prev => ({ ...prev, isOpen: false }))}
          clientId={detailsModal.clientId}
          clientCode={detailsModal.clientCode}
          clientName={detailsModal.clientName}
          orderNumber={detailsModal.orderNumber}
          orderPayload={detailsModal.orderPayload}
          cycleCode={detailsModal.cycleCode}
          scheduledOrderId={detailsModal.scheduledOrderId}
        />
      </div>
    </>
  );
}
